import { TabId } from '@modules/study-insights/types';
import { TFunction } from 'i18next';
import { TabLink } from '@/common/components/base-app/atoms/Tab/Tab.types';
import { FeatureFlags } from '@/modules/feature-flags/types';
import { PATHS } from '@/common/constants';
import { Permissions } from '@modules/permissions/types';
import TooltipByOrganization from '../../components/ParticipantRecluitment/shared/TooltipByOrganization/TooltipByOrganization';
import { PartnerOrganization } from '@/modules/core/types/partnerType.type';

const orgsWithTooltips = [PartnerOrganization.RENOWN, PartnerOrganization.HELIX_RENOWN];

export const getTitleAndTooltip = (
  tabId: string,
  t: TFunction,
  organizationName?: string,
): { title: string; actions?: JSX.Element; withTooltip?: boolean } => {
  const isOrgWithTooltips = orgsWithTooltips.includes(organizationName as PartnerOrganization);
  switch (tabId) {
    case TabId.COLLECTION:
      return {
        title: t('collection_and_sequencing'),
        actions: (
          <TooltipByOrganization
            t={t}
            configuredPartners={orgsWithTooltips}
            direction="column"
            translationsKey="collection_and_sequencing"
          />
        ),
        withTooltip: isOrgWithTooltips,
      };
    case TabId.RECRUITMENT:
      return {
        title: t('participant_recruitment.title'),
        actions: (
          <TooltipByOrganization
            t={t}
            configuredPartners={orgsWithTooltips}
            direction="column"
            translationsKey="participant_recruitment"
          />
        ),
        withTooltip: isOrgWithTooltips,
      };
    case TabId.RESULTS:
      return {
        title: t('delivery_and_results'),
        actions: (
          <TooltipByOrganization
            t={t}
            configuredPartners={orgsWithTooltips}
            direction="column"
            translationsKey="delivery_and_results"
          />
        ),
        withTooltip: isOrgWithTooltips,
      };
    case TabId.SAMPLES:
      return {
        title: t('sample_tracking.title'),
        actions: (
          <TooltipByOrganization
            t={t}
            configuredPartners={orgsWithTooltips}
            direction="column"
            translationsKey="sample_tracking"
          />
        ),
        withTooltip: isOrgWithTooltips,
      };
    default:
      return {
        title: '',
      };
  }
};

export const getTabsList = (
  t: TFunction,
  isFeatureEnabled: (featureFlag: FeatureFlags) => boolean,
  hasPermission: (permission: Permissions) => boolean,
): TabLink[] => {
  const tabsList: TabLink[] = [];

  if (
    isFeatureEnabled(FeatureFlags.PARTICIPANT_RECRUITMENT) &&
    hasPermission(Permissions.STUDY_INSIGHTS__RECRUITMENT_LAYOUT_1)
  ) {
    tabsList.push({
      label: t('participant_recruitment.title'),
      value: TabId.RECRUITMENT,
      url: PATHS.STUDY_INSIGHTS_PAGES.PARTICIPANT_RECRUITMENT,
    });
  }

  if (
    isFeatureEnabled(FeatureFlags.PARTICIPANT_RECRUITMENT) &&
    hasPermission(Permissions.STUDY_INSIGHTS__RECRUITMENT_LAYOUT_3)
  ) {
    tabsList.push({
      label: t('participant_recruitment.title'),
      value: TabId.RECRUITMENT,
      url: PATHS.STUDY_INSIGHTS_PAGES.PARTICIPANT_RECRUITMENT,
    });
  }

  tabsList.push({
    label: t('collection_and_sequencing'),
    value: TabId.COLLECTION,
    url: PATHS.STUDY_INSIGHTS_PAGES.COLLECTION_AND_SEQUENCING,
  });

  if (hasPermission(Permissions.CAN_VIEW_PHI)) {
    tabsList.push({
      label: t('sample_tracking.title'),
      value: TabId.SAMPLES,
      url: PATHS.STUDY_INSIGHTS_PAGES.SAMPLE_TRACKING,
    });
  }
  tabsList.push({
    label: t('delivery_and_results'),
    value: TabId.RESULTS,
    url: PATHS.STUDY_INSIGHTS_PAGES.DELIVERY_AND_RESULTS,
  });

  return tabsList;
};
